// Nearrme store operations list. Stores are physical verified locations under a company.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, EmptyState, LoadingSkeleton } from '@nearrme/ui-web';
import { isNearrmeAdminRole, listCompanies, listStores } from '../api';
import { nextSort, SortHeader, sortRows, SortState } from '../tableSort';
import { useAdminAuth } from '../useAdminAuth';

export function AdminStoresPage() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const isNearrmeAdmin = isNearrmeAdminRole(user?.role);
  const [companyCode, setCompanyCode] = useState(isNearrmeAdmin ? '__all' : user?.companyCode ?? '');
  const [activeOnly, setActiveOnly] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState<'name' | 'company' | 'rating' | 'products' | 'units' | 'status'>>({
    key: 'name',
    direction: 'asc',
  });

  const companies = useQuery({ queryKey: ['admin', 'companies'], queryFn: listCompanies });
  const stores = useQuery({
    queryKey: ['admin', 'stores', companyCode, activeOnly],
    queryFn: () => listStores(companyCode, activeOnly),
    enabled: !!companyCode,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return stores.data ?? [];
    return (stores.data ?? []).filter((store) =>
      [store.name, store.companyCode, store.openingHours, store.languagesSpoken, store.paymentMethods]
        .some((value) => (value ?? '').toLowerCase().includes(q))
    );
  }, [stores.data, search]);

  const sorted = sortRows(filtered, sort, {
    name: (store) => store.name,
    company: (store) => store.companyCode,
    rating: (store) => store.rating,
    products: (store) => store.productCount,
    units: (store) => store.availableUnits,
    status: (store) => store.isActive ? 1 : 0,
  });
  const sortHeader = (label: string, key: typeof sort.key) => (
    <SortHeader label={label} sortKey={key} sort={sort} onSort={(nextKey) => setSort((current) => nextSort(current, nextKey))} />
  );

  return (
    <div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">Stores</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Verified physical store locations, inventory visibility, languages, payments, and operational status.
          </p>
        </div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {isNearrmeAdmin ? 'Network store view' : `Company scope: ${user?.companyCode ?? 'assigned company'}`}
        </div>
      </div>

      <Card padded className="mt-5 border border-slate-200 shadow-sm dark:border-slate-800">
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Company</span>
            <select
              value={companyCode}
              onChange={(event) => setCompanyCode(event.target.value)}
              disabled={!isNearrmeAdmin}
              className="h-10 min-w-60 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:disabled:bg-slate-900"
            >
              {isNearrmeAdmin && <option value="__all">All companies</option>}
              {(companies.data ?? []).map((company) => (
                <option key={company.companyCode ?? ''} value={company.companyCode ?? ''}>
                  {company.companyCode} - {company.companyName}
                </option>
              ))}
            </select>
          </label>
          <label className="block min-w-72 flex-1">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Filter</span>
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Store, company, language, payment, or hours"
              className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            />
          </label>
          <label className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 px-3 text-sm font-medium text-slate-700 dark:border-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(event) => setActiveOnly(event.target.checked)}
            />
            Active only
          </label>
        </div>
      </Card>

      {stores.isLoading && (
        <div className="mt-6 space-y-2">
          {[1, 2, 3, 4, 5].map((item) => <LoadingSkeleton key={item} className="h-12 w-full" />)}
        </div>
      )}

      {stores.error && (
        <Card padded className="mt-6">
          <div className="text-sm text-red-600 dark:text-red-400">{(stores.error as Error).message}</div>
        </Card>
      )}

      {stores.data && (
        <>
          <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            {filtered.length} {filtered.length === 1 ? 'store' : 'stores'}
            {search && ` (filtered from ${stores.data.length})`}
          </div>
          {filtered.length === 0 ? (
            <EmptyState title="No stores" description={search ? 'No stores match your filter.' : 'No store locations are linked to this scope yet.'} />
          ) : (
            <Card padded={false} className="mt-2 overflow-hidden border border-slate-200 shadow-sm dark:border-slate-800">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
                    <tr className="text-left">
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Store', 'name')}</th>
                      {isNearrmeAdmin && <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Company', 'company')}</th>}
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Rating', 'rating')}</th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Products', 'products')}</th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Available units', 'units')}</th>
                      <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Languages</th>
                      <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Payments</th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Status', 'status')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-800 dark:bg-slate-950">
                    {sorted.map((store) => (
                      <tr
                        key={store.storeId}
                        onClick={() => navigate(`/admin/stores/${store.storeId}`, { state: { store } })}
                        tabIndex={0}
                        role="link"
                        className="cursor-pointer hover:bg-slate-50 focus:bg-slate-50 focus:outline-none dark:hover:bg-slate-900 dark:focus:bg-slate-900"
                      >
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-900 dark:text-slate-100">{store.name}</div>
                          <div className="mt-1 font-mono text-xs text-slate-500 dark:text-slate-400">#{store.storeId}</div>
                          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{store.openingHours ?? 'Hours not configured'}</div>
                        </td>
                        {isNearrmeAdmin && <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-400">{store.companyCode}</td>}
                        <td className="px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">{store.rating.toFixed(1)}</td>
                        <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{store.productCount}</td>
                        <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{store.availableUnits}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{store.languagesSpoken}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{store.paymentMethods}</td>
                        <td className="px-4 py-3">
                          {store.isActive ? (
                            <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-700 dark:bg-green-950 dark:text-green-400">Active</span>
                          ) : (
                            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">Inactive</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export function AdminStoreDetailPage() {
  const { user } = useAdminAuth();
  const { storeId } = useParams();
  const isNearrmeAdmin = isNearrmeAdminRole(user?.role);
  const companyCode = isNearrmeAdmin ? '__all' : user?.companyCode ?? '';
  const stores = useQuery({
    queryKey: ['admin', 'stores', companyCode, true],
    queryFn: () => listStores(companyCode, true),
    enabled: !!companyCode,
  });
  const store = (stores.data ?? []).find((item) => String(item.storeId) === String(storeId));

  if (stores.isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((item) => <LoadingSkeleton key={item} className="h-12 w-full" />)}
      </div>
    );
  }

  if (stores.error) {
    return (
      <Card padded>
        <div className="text-sm text-red-600 dark:text-red-400">{(stores.error as Error).message}</div>
      </Card>
    );
  }

  if (!store) {
    return <EmptyState title="Store not found" description="This store is not available in your assigned scope." />;
  }

  return (
    <div>
      <h1 className="text-3xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">{store.name}</h1>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        Store profile, inventory summary, language coverage, payments, and map coordinates.
      </p>
      <div className="mt-6 grid gap-4 md:grid-cols-4">
        <Metric label="Products" value={store.productCount} />
        <Metric label="Available units" value={store.availableUnits} />
        <Metric label="Rating" value={store.rating.toFixed(1)} />
        <Metric label="Status" value={store.isActive ? 'Active' : 'Inactive'} />
      </div>
      <Card padded className="mt-6 border border-slate-200 shadow-sm dark:border-slate-800">
        <div className="grid gap-4 md:grid-cols-2">
          <Detail label="Store ID" value={String(store.storeId)} />
          <Detail label="Company" value={store.companyCode} />
          <Detail label="Opening hours" value={store.openingHours ?? 'Not configured'} />
          <Detail label="Languages spoken" value={store.languagesSpoken} />
          <Detail label="Payment methods" value={store.paymentMethods} />
          <Detail label="Coordinates" value={`${store.latitude.toFixed(5)}, ${store.longitude.toFixed(5)}`} />
        </div>
      </Card>
    </div>
  );
}

function Metric({ label, value }: { label: string; value?: string | number }) {
  return (
    <Card padded className="border border-slate-200 shadow-sm dark:border-slate-800">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-slate-950 dark:text-slate-50">{value ?? '-'}</div>
    </Card>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}
